import type { ReactNode } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { useForm } from '@tanstack/react-form';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import { AlertCircle } from '@untitledui/icons/AlertCircle';
import { CheckCircle } from '@untitledui/icons/CheckCircle';
import { z } from 'zod';

import { Button, LinkButton } from '@/components/base/buttons/button';
import { FormInput } from '@/components/base/form/form-input';
import { AuthLayout } from '@/routes/participants/-components/auth-layout';
import { DecorativeCircles } from '@/routes/participants/-components/decorative-circles';
import { serverFetch } from '@/utils/server-fetch';

type LoginPath = '/participants/login' | '/researchers/login';

interface ResetPasswordPageProps {
  token: string;
  tokenValid: boolean;
  loginPath: LoginPath;
  rightPanel?: ReactNode;
  successIconClassName?: string;
}

export const verifyResetToken = createServerFn()
  .validator((data: { token: string }) => data)
  .handler(async ({ data: { token } }) => {
    try {
      const response = await serverFetch({
        path: '/auth/password-token/verify',
        method: 'POST',
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        return { valid: false as const };
      }

      return { valid: true as const };
    } catch {
      return { valid: false as const };
    }
  });

const resetPassword = createServerFn()
  .validator((data: { token: string; password: string }) => data)
  .handler(async ({ data: { token, password } }) => {
    try {
      const tokenCheck = await verifyResetToken({ data: { token } });
      if (!tokenCheck.valid) {
        return { success: false as const, error: 'expired' };
      }

      const response = await serverFetch({
        path: '/auth/reset-password',
        method: 'POST',
        body: JSON.stringify({ password, token }),
      });

      if (!response.ok) {
        return { success: false as const, error: 'reset_failed' };
      }

      return { success: true as const };
    } catch (error) {
      console.error('Password reset error:', error);
      return { success: false as const, error: 'unexpected' };
    }
  });

export function ResetPasswordPage({
  token,
  tokenValid,
  loginPath,
  rightPanel,
  successIconClassName = 'text-white',
}: ResetPasswordPageProps) {
  if (!tokenValid) {
    return <TokenExpiredView rightPanel={rightPanel} />;
  }

  return (
    <PasswordForm
      token={token}
      loginPath={loginPath}
      rightPanel={rightPanel}
      successIconClassName={successIconClassName}
    />
  );
}

function TokenExpiredView({
  rightPanel,
}: Pick<ResetPasswordPageProps, 'rightPanel'>) {
  return (
    <AuthLayout rightPanel={rightPanel}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles badgeClassName="bg-warning-secondary">
          <AlertCircle className="size-7 text-white" />
        </DecorativeCircles>

        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          <Trans>Link expired</Trans>
        </h1>

        <p className="z-10 mt-3 text-md text-primary">
          <Trans>
            This password reset link has expired or is invalid. Please request a
            new one.
          </Trans>
        </p>

        <div className="z-10 mt-8">
          <LinkButton color="primary" size="lg" to="/forgot-password">
            <Trans>Request new link</Trans>
          </LinkButton>
        </div>
      </div>
    </AuthLayout>
  );
}

function PasswordForm({
  token,
  loginPath,
  rightPanel,
  successIconClassName,
}: Pick<
  ResetPasswordPageProps,
  'token' | 'loginPath' | 'rightPanel' | 'successIconClassName'
>) {
  const { t } = useLingui();

  const resetPasswordSchema = z
    .object({
      password: z.string().min(8, t`Password must be at least 8 characters`),
      confirmPassword: z.string().min(1, t`Please confirm your password`),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t`Passwords do not match`,
      path: ['confirmPassword'],
    });

  const mutation = useMutation({
    mutationFn: async (value: { password: string }) => {
      const result = await resetPassword({
        data: { token, password: value.password },
      });

      if (result.success) {
        return null;
      } else if (result.error === 'expired') {
        throw new Error(
          t`This reset link has expired. Please request a new one.`,
        );
      } else {
        throw new Error(t`An unexpected error occurred. Please try again.`);
      }
    },
  });

  const form = useForm({
    defaultValues: { password: '', confirmPassword: '' },
    validators: { onSubmit: resetPasswordSchema },
    onSubmit: ({ value }) => mutation.mutateAsync({ password: value.password }),
  });

  if (mutation.isSuccess) {
    return (
      <PasswordResetSuccess
        loginPath={loginPath}
        rightPanel={rightPanel}
        successIconClassName={successIconClassName}
      />
    );
  }

  return (
    <AuthLayout rightPanel={rightPanel}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void form.handleSubmit();
        }}
        className="flex flex-col gap-8"
      >
        <div>
          <h1 className="font-heading text-display-xs font-semibold text-primary">
            <Trans>Set new password</Trans>
          </h1>
          <p className="mt-1 text-md text-secondary">
            <Trans>Your new password must be at least 8 characters.</Trans>
          </p>
        </div>

        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-5">
            <FormInput
              label={t`New password`}
              placeholder="••••••••"
              type="password"
              form={form}
              field="password"
              size="md"
            />
            <FormInput
              label={t`Confirm password`}
              placeholder="••••••••"
              type="password"
              form={form}
              field="confirmPassword"
              size="md"
            />
          </div>

          {mutation.error && (
            <p className="text-sm text-error-primary">
              {mutation.error.message}
            </p>
          )}

          <form.Subscribe
            selector={(state) => state.isSubmitting}
            children={(isSubmitting) => (
              <Button
                type="submit"
                color="primary"
                size="lg"
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                className="w-full"
              >
                <Trans>Reset password</Trans>
              </Button>
            )}
          />
        </div>

        <p className="text-sm text-tertiary">
          <Link
            to={loginPath}
            className="font-semibold text-brand-secondary hover:text-brand-secondary_hover"
          >
            <Trans>Back to log in</Trans>
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

function PasswordResetSuccess({
  loginPath,
  rightPanel,
  successIconClassName = 'text-white',
}: Pick<
  ResetPasswordPageProps,
  'loginPath' | 'rightPanel' | 'successIconClassName'
>) {
  return (
    <AuthLayout rightPanel={rightPanel}>
      <div className="flex flex-col items-center text-center">
        <DecorativeCircles badgeClassName="bg-success-secondary">
          <CheckCircle className={`size-7 ${successIconClassName}`} />
        </DecorativeCircles>

        <h1 className="z-10 mt-2 font-heading text-display-sm font-semibold text-primary">
          <Trans>Password reset!</Trans>
        </h1>

        <p className="z-10 mt-3 text-md text-primary">
          <Trans>
            Your password has been successfully reset. Please log in with your
            new password.
          </Trans>
        </p>

        <div className="z-10 mt-8">
          <LinkButton color="primary" size="lg" to={loginPath}>
            <Trans>Go to log in</Trans>
          </LinkButton>
        </div>
      </div>
    </AuthLayout>
  );
}
